import React, { useState, useEffect } from "react";
import { Plus, Save, Copy } from "lucide-react";
import { episodesApi, podcastsApi } from "../../utils/api";
import SearchBar from "../common/SearchBar";
import FormModal from "../common/FormModal";
import ActionButtons, {
  PrimaryButton,
  SecondaryButton,
} from "../common/ActionButtons";

const EpisodeManagement = () => {
  const [episodes, setEpisodes] = useState([]);
  const [podcasts, setPodcasts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingEpisode, setEditingEpisode] = useState(null);

  const [formData, setFormData] = useState({
    status: "Draft",
    title: "",
    topic: "",
    podCastID: 0,
  });

  const statusOptions = [
    "Draft",
    "In Progress",
    "Ready",
    "Published",
    "Archived",
  ];

  useEffect(() => {
    fetchEpisodes();
    fetchPodcasts();
  }, []);

  const fetchEpisodes = async () => {
    setLoading(true);
    try {
      const data = await episodesApi.getAll();
      setEpisodes(data);
    } catch (error) {
      console.error("Error fetching episodes:", error);
    }
    setLoading(false);
  };

  const fetchPodcasts = async () => {
    try {
      const data = await podcastsApi.getAll();
      setPodcasts(data);
    } catch (error) {
      console.error("Error fetching podcasts:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingEpisode) {
        await episodesApi.update(editingEpisode.id, formData);
      } else {
        await episodesApi.create(formData);
      }
      fetchEpisodes();
      resetForm();
    } catch (error) {
      console.error("Error saving episode:", error);
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this episode?")) return;
    setLoading(true);
    try {
      await episodesApi.delete(id);
      fetchEpisodes();
    } catch (error) {
      console.error("Error deleting episode:", error);
    }
    setLoading(false);
  };

  const handleCopy = async (id) => {
    if (!window.confirm("Copy this episode with all hosts and prompts?"))
      return;
    setLoading(true);
    try {
      await episodesApi.copy(id);
      fetchEpisodes();
    } catch (error) {
      console.error("Error copying episode:", error);
    }
    setLoading(false);
  };

  const handleEdit = (episode) => {
    setEditingEpisode(episode);
    setFormData({
      status: episode.status || "Draft",
      title: episode.title || "",
      topic: episode.topic || "",
      podCastID: episode.podCastID || 0,
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      status: "Draft",
      title: "",
      topic: "",
      podCastID: 0,
    });
    setEditingEpisode(null);
    setShowForm(false);
  };

  const getPodcastName = (podCastID) => {
    const podcast = podcasts.find((p) => p.id === podCastID);
    return podcast ? podcast.title : "N/A";
  };

  const filteredEpisodes = episodes.filter(
    (episode) =>
      episode.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      episode.topic?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <SearchBar
          value={searchTerm}
          onChange={setSearchTerm}
          onRefresh={fetchEpisodes}
          placeholder="Search episodes..."
        />
        <PrimaryButton onClick={() => setShowForm(!showForm)} icon={Plus}>
          New Episode
        </PrimaryButton>
      </div>

      <FormModal
        title={editingEpisode ? "Edit Episode" : "New Episode"}
        isOpen={showForm}
        onClose={resetForm}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Podcast *
            </label>
            <select
              required
              value={formData.podCastID}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  podCastID: parseInt(e.target.value),
                })
              }
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value={0}>Select Podcast</option>
              {podcasts.map((podcast) => (
                <option key={podcast.id} value={podcast.id}>
                  {podcast.title}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Title *
            </label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Topic
            </label>
            <textarea
              value={formData.topic}
              onChange={(e) =>
                setFormData({ ...formData, topic: e.target.value })
              }
              rows={3}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Status
            </label>
            <select
              value={formData.status}
              onChange={(e) =>
                setFormData({ ...formData, status: e.target.value })
              }
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {statusOptions.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <SecondaryButton onClick={resetForm}>Cancel</SecondaryButton>
            <PrimaryButton type="submit" disabled={loading} icon={Save}>
              {loading ? "Saving..." : "Save Episode"}
            </PrimaryButton>
          </div>
        </form>
      </FormModal>

      <div className="bg-white rounded-lg shadow-md overflow-hidden border border-slate-200">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                Title
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                Podcast
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                Topic
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-200">
            {loading ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-6 py-4 text-center text-slate-500"
                >
                  Loading...
                </td>
              </tr>
            ) : filteredEpisodes.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-6 py-4 text-center text-slate-500"
                >
                  No episodes found. Create one to get started!
                </td>
              </tr>
            ) : (
              filteredEpisodes.map((episode) => (
                <tr key={episode.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4 text-sm font-medium text-slate-900">
                    {episode.title}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">
                    {getPodcastName(episode.podCastID)}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600 max-w-xs truncate">
                    {episode.topic || "-"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        episode.status === "Published"
                          ? "bg-green-100 text-green-800"
                          : episode.status === "Ready"
                            ? "bg-blue-100 text-blue-800"
                            : episode.status === "In Progress"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-slate-100 text-slate-800"
                      }`}
                    >
                      {episode.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                    <div className="flex items-center space-x-2">
                      <ActionButtons
                        onEdit={() => handleEdit(episode)}
                        onDelete={() => handleDelete(episode.id)}
                      />
                      <button
                        onClick={() => handleCopy(episode.id)}
                        className="p-1 text-slate-600 hover:text-blue-600 transition-colors"
                        title="Copy Episode"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EpisodeManagement;
